import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const en = {
  common: {
    cancel: 'Cancel', save: 'Save', close: 'Close', search: 'Search',
    signInGoogle: 'Sign in with Google',
  },
  nav: {
    home: 'Home', about: 'About', contact: 'Contact',
    myPins: 'My pins', account: 'Account settings', admin: 'Admin',
    moderation: 'Moderation', signOut: 'Sign out', language: 'Language', donate: 'Donate',
  },
  filter: { list: 'List', all: 'All', free: 'Free Items', yard_sale: 'Yard Sales' },
  list: {
    pins_one: '{{count}} pin in this area',
    pins_other: '{{count}} pins in this area',
    zoomIn: 'Zoom in', zoomOut: 'Zoom out', closeList: 'Close list',
    sortNewest: 'Newest', sortNearest: 'Nearest', sortEnding: 'Ending soon',
    empty: 'No pins in view. Zoom out or pan the map to find more.',
    new: 'New', endingSoon: 'Ending soon',
    free: 'Free', yardSale: 'Yard Sale',
  },
  map: {
    loading: 'Finding your neighborhood…',
    dropPin: 'Drop a Pin', myPins: 'My Pins',
    signInToDrop: 'Sign in to drop a pin',
    pinDropped: 'Pin dropped! It expires in 72 hours.',
    geoUnsupported: 'Location is not supported on this device.',
    geoDenied: 'Location is blocked. Enable it for this site in your browser settings, then tap the locate button.',
    geoUnavailable: 'Could not get your location. Showing a default area.',
  },
  hero: {
    badge: 'Neighborhood map',
    title: 'Your neighborhood, on the map.',
    subtitle: 'Find free curbside finds, garage sales, and treasures nearby.',
    cta: 'Sign in with Google to drop your first pin.',
    expand: 'Sign in',
    minimize: 'Minimize',
  },
  create: {
    editTitle: 'Edit Pin', createTitle: 'Drop a Pin',
    editSubtitle: 'Update your pin details.',
    createSubtitle: 'Double-click the map, drag the green pin, or set an address below.',
    category: 'Category', free: 'Free', yardSale: 'Yard Sale',
    title: 'Title', titlePlaceholder: 'Free couch, books, lamp...',
    titleProfanity: 'Please remove inappropriate language from the title.',
    description: 'Description',
    descPlaceholder: 'Tell your neighbors what it is, condition, when to grab it...',
    descProfanity: 'Please remove inappropriate language from the description.',
    address: 'Address / location', addressPlaceholder: '123 Main St, Brooklyn...',
    showAddress: 'Show address publicly',
    showAddressHint: 'Off = only an approximate pin is shown.',
    whenActive: 'When is this active?', todayOnly: 'Today only',
    startDate: 'Start date', endDate: 'End date',
    startTime: 'Start time', endTime: 'End time',
    windowHint: 'Max active window: 2 days. Can start up to 2 weeks ahead.',
    photos: 'Photos (up to {{count}})',
    pinLocation: 'Pin location: {{coords}}',
    saveChanges: 'Save changes', dropPin: 'Drop Pin',
    addTitle: 'Add a title',
    profanityError: 'Please remove inappropriate language before posting.',
    pickSpot: 'Pick a spot on the map first',
    photoRequired: 'A photo is required for free items',
    uploadFailed: 'Upload failed', couldNotCreate: 'Could not create pin',
  },
  welcome: {
    whatYouCanDo: 'What you can do', communityRules: 'Community rules',
    chooseUsername: 'Choose your username',
    usernamePlaceholder: 'At least 6 characters',
    usernameHint: '6–24 characters · letters, numbers, _ or - · this is what neighbors see (your real name stays private).',
    noRealName: 'For your safety, don’t use your real or full name — pick a nickname instead.',
    profanityHint: 'Please choose a username without inappropriate language.',
    reservedHint: 'That username is reserved. Please choose another.',
    agreeStart: 'Agree & start exploring',
    agreeLabel: 'I have read and agree to the community rules above.',
    titleDefault: 'Welcome to CurbAlert',
    agreeError: 'Please agree to the community rules',
    usernameError: 'Username must be at least 6 characters (letters, numbers, _ or -)',
    welcomeAboard: 'Welcome aboard, @{{username}}!',
    setupError: 'Could not complete setup',
  },
  rename: {
    title: 'Choose a new username',
    body: 'A moderator has asked you to pick a new username before continuing.',
    submit: 'Save new username',
    sameAsOld: 'Please choose a different username than your current one.',
    done: 'Username updated — thanks!',
  },
  notif: {
    title: 'Notifications',
    empty: "You're all caught up.",
    nearbyTitle: 'New near you',
    ticketsTitle: 'Needs your support',
    accountTitle: 'Your account',
    clear: 'Clear',
    milesAway: '{{n}} mi away',
    viewOnMap: 'View on map',
  },
  detail: {
    free: 'Free', yardSale: 'Yard Sale',
    upcoming: 'Upcoming', activeUntil: 'Active until',
    hidden: 'Hidden — needs your action', ended: 'Ended',
    new: 'New', endingSoon: 'Ending soon',
    reportedGone: 'Reported gone by neighbors',
    reportedGoneHint: "Confirm it's still here, or it'll be archived in ~24 hours.",
    stillHere: "It's still here",
    stillHereVisible: 'Pin is visible again',
    stillQuestionItem: 'Is this item still here?',
    stillQuestionYard: 'Is this yard sale still here?',
    yes: 'Yes', no: 'No',
    voteHint: '5 "No" reports auto-hide the pin.',
    edit: 'Edit', delete: 'Delete',
    getDirections: 'Get directions', reportAd: 'Report Ad',
    confirmDelete: 'Delete this pin?',
    deleted: 'Pin deleted', couldNotDelete: 'Could not delete',
    voteGone: 'Thanks — reported as gone', voteHere: 'Thanks for confirming',
    voteHidden: 'This pin reached 5 "gone" reports and is now hidden.',
    couldNotVote: 'Could not record vote',
    linkCopied: 'Link copied to clipboard', couldNotShare: 'Could not share link',
    shareTitle: 'Share this pin',
  },
};

const es = {
  common: {
    cancel: 'Cancelar', save: 'Guardar', close: 'Cerrar', search: 'Buscar',
    signInGoogle: 'Iniciar sesión con Google',
  },
  nav: {
    home: 'Inicio', about: 'Acerca de', contact: 'Contacto',
    myPins: 'Mis pines', account: 'Configuración de cuenta', admin: 'Administración',
    moderation: 'Moderación', signOut: 'Cerrar sesión', language: 'Idioma', donate: 'Donar',
  },
  filter: { list: 'Lista', all: 'Todos', free: 'Artículos gratis', yard_sale: 'Ventas de garaje' },
  list: {
    pins_one: '{{count}} pin en esta zona',
    pins_other: '{{count}} pines en esta zona',
    zoomIn: 'Acercar', zoomOut: 'Alejar', closeList: 'Cerrar lista',
    sortNewest: 'Más recientes', sortNearest: 'Más cercanos', sortEnding: 'Por terminar',
    empty: 'No hay pines a la vista. Aleja o mueve el mapa para ver más.',
    new: 'Nuevo', endingSoon: 'Termina pronto',
    free: 'Gratis', yardSale: 'Venta de garaje',
  },
  map: {
    loading: 'Buscando tu vecindario…',
    dropPin: 'Soltar un pin', myPins: 'Mis pines',
    signInToDrop: 'Inicia sesión para soltar un pin',
    pinDropped: '¡Pin colocado! Caduca en 72 horas.',
    geoUnsupported: 'La ubicación no es compatible con este dispositivo.',
    geoDenied: 'La ubicación está bloqueada. Actívala para este sitio en la configuración del navegador y luego toca el botón de ubicación.',
    geoUnavailable: 'No se pudo obtener tu ubicación. Mostrando una zona predeterminada.',
  },
  hero: {
    badge: 'Mapa del vecindario',
    title: 'Tu vecindario, en el mapa.',
    subtitle: 'Encuentra hallazgos gratis en la acera, ventas de garaje y tesoros cercanos.',
    cta: 'Inicia sesión con Google para colocar tu primer pin.',
    expand: 'Iniciar sesión',
    minimize: 'Minimizar',
  },
  create: {
    editTitle: 'Editar pin', createTitle: 'Soltar un pin',
    editSubtitle: 'Actualiza los detalles de tu pin.',
    createSubtitle: 'Haz doble clic en el mapa, arrastra el pin verde o escribe una dirección abajo.',
    category: 'Categoría', free: 'Gratis', yardSale: 'Venta de garaje',
    title: 'Título', titlePlaceholder: 'Sofá gratis, libros, lámpara...',
    titleProfanity: 'Por favor, quita el lenguaje inapropiado del título.',
    description: 'Descripción',
    descPlaceholder: 'Cuéntales a tus vecinos qué es, su estado y cuándo recogerlo...',
    descProfanity: 'Por favor, quita el lenguaje inapropiado de la descripción.',
    address: 'Dirección / ubicación', addressPlaceholder: 'Calle Principal 123...',
    showAddress: 'Mostrar dirección públicamente',
    showAddressHint: 'Desactivado = solo se muestra un pin aproximado.',
    whenActive: '¿Cuándo está activo?', todayOnly: 'Solo hoy',
    startDate: 'Fecha de inicio', endDate: 'Fecha de fin',
    startTime: 'Hora de inicio', endTime: 'Hora de fin',
    windowHint: 'Ventana activa máx.: 2 días. Puede empezar hasta 2 semanas después.',
    photos: 'Fotos (hasta {{count}})',
    pinLocation: 'Ubicación del pin: {{coords}}',
    saveChanges: 'Guardar cambios', dropPin: 'Soltar pin',
    addTitle: 'Añade un título',
    profanityError: 'Por favor, quita el lenguaje inapropiado antes de publicar.',
    pickSpot: 'Primero elige un lugar en el mapa',
    photoRequired: 'Se requiere una foto para artículos gratis',
    uploadFailed: 'Error al subir', couldNotCreate: 'No se pudo crear el pin',
  },
  welcome: {
    whatYouCanDo: 'Lo que puedes hacer', communityRules: 'Reglas de la comunidad',
    chooseUsername: 'Elige tu nombre de usuario',
    usernamePlaceholder: 'Al menos 6 caracteres',
    usernameHint: '6–24 caracteres · letras, números, _ o - · esto es lo que ven los vecinos (tu nombre real es privado).',
    noRealName: 'Por tu seguridad, no uses tu nombre real ni completo: elige un apodo en su lugar.',
    profanityHint: 'Por favor, elige un nombre de usuario sin lenguaje inapropiado.',
    reservedHint: 'Ese nombre de usuario está reservado. Elige otro.',
    agreeStart: 'Aceptar y empezar a explorar',
    agreeLabel: 'He leído y acepto las reglas de la comunidad anteriores.',
    titleDefault: 'Bienvenido a CurbAlert',
    agreeError: 'Por favor, acepta las reglas de la comunidad',
    usernameError: 'El nombre de usuario debe tener al menos 6 caracteres (letras, números, _ o -)',
    welcomeAboard: '¡Bienvenido, @{{username}}!',
    setupError: 'No se pudo completar la configuración',
  },
  rename: {
    title: 'Elige un nuevo nombre de usuario',
    body: 'Un moderador te ha pedido que elijas un nuevo nombre de usuario antes de continuar.',
    submit: 'Guardar nuevo nombre',
    sameAsOld: 'Por favor, elige un nombre de usuario diferente al actual.',
    done: 'Nombre de usuario actualizado. ¡Gracias!',
  },
  notif: {
    title: 'Notificaciones',
    empty: 'Estás al día.',
    nearbyTitle: 'Nuevo cerca de ti',
    ticketsTitle: 'Necesita tu apoyo',
    accountTitle: 'Tu cuenta',
    clear: 'Borrar',
    milesAway: 'a {{n}} mi',
    viewOnMap: 'Ver en el mapa',
  },
  detail: {
    free: 'Gratis', yardSale: 'Venta de garaje',
    upcoming: 'Próximamente', activeUntil: 'Activo hasta',
    hidden: 'Oculto — requiere tu acción', ended: 'Finalizado',
    new: 'Nuevo', endingSoon: 'Termina pronto',
    reportedGone: 'Reportado como ido por los vecinos',
    reportedGoneHint: 'Confirma que sigue aquí, o se archivará en ~24 horas.',
    stillHere: 'Sigue aquí',
    stillHereVisible: 'El pin vuelve a ser visible',
    stillQuestionItem: '¿Este artículo sigue aquí?',
    stillQuestionYard: '¿Esta venta de garaje sigue activa?',
    yes: 'Sí', no: 'No',
    voteHint: '5 reportes de "No" ocultan el pin automáticamente.',
    edit: 'Editar', delete: 'Eliminar',
    getDirections: 'Cómo llegar', reportAd: 'Reportar anuncio',
    confirmDelete: '¿Eliminar este pin?',
    deleted: 'Pin eliminado', couldNotDelete: 'No se pudo eliminar',
    voteGone: 'Gracias — reportado como ido', voteHere: 'Gracias por confirmar',
    voteHidden: 'Este pin alcanzó 5 reportes de "ido" y ahora está oculto.',
    couldNotVote: 'No se pudo registrar el voto',
    linkCopied: 'Enlace copiado al portapapeles', couldNotShare: 'No se pudo compartir el enlace',
    shareTitle: 'Compartir este pin',
  },
};

const fr = {
  common: {
    cancel: 'Annuler', save: 'Enregistrer', close: 'Fermer', search: 'Rechercher',
    signInGoogle: 'Se connecter avec Google',
  },
  nav: {
    home: 'Accueil', about: 'À propos', contact: 'Contact',
    myPins: 'Mes épingles', account: 'Paramètres du compte', admin: 'Admin',
    moderation: 'Modération', signOut: 'Se déconnecter', language: 'Langue', donate: 'Faire un don',
  },
  filter: { list: 'Liste', all: 'Tous', free: 'Articles gratuits', yard_sale: 'Vide-greniers' },
  list: {
    pins_one: '{{count}} épingle dans cette zone',
    pins_other: '{{count}} épingles dans cette zone',
    zoomIn: 'Zoom avant', zoomOut: 'Zoom arrière', closeList: 'Fermer la liste',
    sortNewest: 'Plus récents', sortNearest: 'Plus proches', sortEnding: 'Bientôt finis',
    empty: 'Aucune épingle en vue. Dézoomez ou déplacez la carte pour en trouver.',
    new: 'Nouveau', endingSoon: 'Bientôt fini',
    free: 'Gratuit', yardSale: 'Vide-grenier',
  },
  map: {
    loading: 'Recherche de votre quartier…',
    dropPin: 'Déposer une épingle', myPins: 'Mes épingles',
    signInToDrop: 'Connectez-vous pour déposer une épingle',
    pinDropped: 'Épingle déposée ! Elle expire dans 72 heures.',
    geoUnsupported: 'La localisation n’est pas prise en charge sur cet appareil.',
    geoDenied: 'La localisation est bloquée. Activez-la pour ce site dans les paramètres du navigateur, puis appuyez sur le bouton de localisation.',
    geoUnavailable: 'Impossible d’obtenir votre position. Affichage d’une zone par défaut.',
  },
  hero: {
    badge: 'Carte du quartier',
    title: 'Votre quartier, sur la carte.',
    subtitle: 'Trouvez des trouvailles gratuites au bord du trottoir, des vide-greniers et des trésors à proximité.',
    cta: 'Connectez-vous avec Google pour déposer votre première épingle.',
    expand: 'Se connecter',
    minimize: 'Réduire',
  },
  create: {
    editTitle: 'Modifier l’épingle', createTitle: 'Déposer une épingle',
    editSubtitle: 'Mettez à jour les détails de votre épingle.',
    createSubtitle: 'Double-cliquez sur la carte, faites glisser l’épingle verte ou saisissez une adresse ci-dessous.',
    category: 'Catégorie', free: 'Gratuit', yardSale: 'Vide-grenier',
    title: 'Titre', titlePlaceholder: 'Canapé gratuit, livres, lampe...',
    titleProfanity: 'Veuillez retirer le langage inapproprié du titre.',
    description: 'Description',
    descPlaceholder: 'Dites à vos voisins ce que c’est, son état, quand le récupérer...',
    descProfanity: 'Veuillez retirer le langage inapproprié de la description.',
    address: 'Adresse / lieu', addressPlaceholder: '123 rue Principale...',
    showAddress: 'Afficher l’adresse publiquement',
    showAddressHint: 'Désactivé = seule une épingle approximative est affichée.',
    whenActive: 'Quand est-ce actif ?', todayOnly: 'Aujourd’hui seulement',
    startDate: 'Date de début', endDate: 'Date de fin',
    startTime: 'Heure de début', endTime: 'Heure de fin',
    windowHint: 'Fenêtre active max. : 2 jours. Peut commencer jusqu’à 2 semaines à l’avance.',
    photos: 'Photos (jusqu’à {{count}})',
    pinLocation: 'Emplacement : {{coords}}',
    saveChanges: 'Enregistrer', dropPin: 'Déposer',
    addTitle: 'Ajoutez un titre',
    profanityError: 'Veuillez retirer le langage inapproprié avant de publier.',
    pickSpot: 'Choisissez d’abord un endroit sur la carte',
    photoRequired: 'Une photo est requise pour les articles gratuits',
    uploadFailed: 'Échec du téléversement', couldNotCreate: 'Impossible de créer l’épingle',
  },
  welcome: {
    whatYouCanDo: 'Ce que vous pouvez faire', communityRules: 'Règles de la communauté',
    chooseUsername: 'Choisissez votre nom d’utilisateur',
    usernamePlaceholder: 'Au moins 6 caractères',
    usernameHint: '6 à 24 caractères · lettres, chiffres, _ ou - · c’est ce que voient les voisins (votre vrai nom reste privé).',
    noRealName: 'Pour votre sécurité, n’utilisez pas votre vrai nom ou nom complet — choisissez plutôt un pseudonyme.',
    profanityHint: 'Veuillez choisir un nom d’utilisateur sans langage inapproprié.',
    reservedHint: 'Ce nom d’utilisateur est réservé. Veuillez en choisir un autre.',
    agreeStart: 'Accepter et commencer à explorer',
    agreeLabel: 'J’ai lu et j’accepte les règles de la communauté ci-dessus.',
    titleDefault: 'Bienvenue sur CurbAlert',
    agreeError: 'Veuillez accepter les règles de la communauté',
    usernameError: 'Le nom d’utilisateur doit comporter au moins 6 caractères (lettres, chiffres, _ ou -)',
    welcomeAboard: 'Bienvenue, @{{username}} !',
    setupError: 'Impossible de terminer la configuration',
  },
  rename: {
    title: 'Choisissez un nouveau nom d’utilisateur',
    body: 'Un modérateur vous a demandé de choisir un nouveau nom d’utilisateur avant de continuer.',
    submit: 'Enregistrer le nouveau nom',
    done: 'Nom d’utilisateur mis à jour — merci !',
  },
  notif: {
    title: 'Notifications',
    empty: 'Vous êtes à jour.',
    nearbyTitle: 'Nouveau près de vous',
    ticketsTitle: 'Besoin de votre aide',
    accountTitle: 'Votre compte',
    clear: 'Effacer',
    milesAway: 'à {{n}} mi',
    viewOnMap: 'Voir sur la carte',
  },
  detail: {
    free: 'Gratuit', yardSale: 'Vide-grenier',
    upcoming: 'À venir', activeUntil: 'Actif jusqu’au',
    hidden: 'Masqué — action requise', ended: 'Terminé',
    new: 'Nouveau', endingSoon: 'Bientôt fini',
    reportedGone: 'Signalé comme parti par les voisins',
    reportedGoneHint: 'Confirmez qu’il est encore là, sinon il sera archivé dans ~24 heures.',
    stillHere: 'C’est encore là',
    stillHereVisible: 'L’épingle est de nouveau visible',
    stillQuestionItem: 'Cet article est-il encore là ?',
    stillQuestionYard: 'Ce vide-grenier est-il encore actif ?',
    yes: 'Oui', no: 'Non',
    voteHint: '5 signalements « Non » masquent l’épingle automatiquement.',
    edit: 'Modifier', delete: 'Supprimer',
    getDirections: 'Itinéraire', reportAd: 'Signaler l’annonce',
    confirmDelete: 'Supprimer cette épingle ?',
    deleted: 'Épingle supprimée', couldNotDelete: 'Impossible de supprimer',
    voteGone: 'Merci — signalé comme parti', voteHere: 'Merci d’avoir confirmé',
    voteHidden: 'Cette épingle a atteint 5 signalements « parti » et est maintenant masquée.',
    couldNotVote: 'Impossible d’enregistrer le vote',
    linkCopied: 'Lien copié dans le presse-papiers', couldNotShare: 'Impossible de partager le lien',
    shareTitle: 'Partager cette épingle',
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, es: { translation: es }, fr: { translation: fr } },
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'fr'],
    interpolation: { escapeValue: false },
    detection: { order: ['querystring', 'localStorage', 'navigator'], lookupQuerystring: 'lng', caches: ['localStorage'] },
  });

export const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
];

export default i18n;
